import { useEffect, useState, forwardRef } from 'react';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import DOMPurify from 'dompurify';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { useMediaQuery } from '@/hooks/use-media-query';
import { useDebounceValue } from '@/hooks/use-debounce.hook';
import { Button } from '@/components/ui/button/button.component';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.component';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer.component';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.component';
import { searchCitiesByTerm } from '@/services/cities.service';
import { City } from '@/types/city.type';
import { getLogger } from '@/logger/logger';

type SelectCitiesProps = {
  selectedCity: City | null;
  onChange: (city: City | null) => void;
  hasError?: boolean;
};

const SelectCities = forwardRef<HTMLButtonElement, SelectCitiesProps>(
  ({ selectedCity, onChange, hasError, ...props }, ref) => {
    const [open, setOpen] = useState(false);
    const isDesktop = useMediaQuery('(min-width: 640px)');

    if (isDesktop) {
      return (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              {...props}
              ref={ref}
              data-testid={DataTestIdEnum.SELECT_CITIES_BUTTON_TRIGGER_SELECT_CITIES}
              variant="outline"
              className={`bg-white hover:bg-gray-50 justify-between shrink-0 transition-transform duration-200 text-base shadow-tooltip border h-12
                          outline-none focus:border-blue-300 mt-1
                        ${open && !selectedCity ? 'border-blue-300' : 'border-gray-300'}
                        ${selectedCity ? 'border-gray-300' : ''}
                        ${hasError && !open ? 'border-danger-600 focus:border-danger-600 mb-2' : ''}
                        `}
              endIcon={open ? faChevronUp : faChevronDown}
              endIconClassName="text-gray-400"
            >
              {selectedCity
                ? (
                    <span className="text-gray-900">{`${selectedCity.city} - ${selectedCity.uf}`}</span>
                  )
                : (
                    <span className="text-gray-400">Selecione uma opção</span>
                  )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 bg-white" align="start">
            <StatusList setOpen={setOpen} setSelectedCity={onChange} />
          </PopoverContent>
        </Popover>
      );
    }

    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          <Button
            {...props}
            ref={ref}
            variant="outline"
            className={`bg-white hover:bg-gray-50 justify-between shrink-0 transition-transform duration-200 text-base text-gray-400 shadow-tooltip border h-12
            outline-none focus:border-blue-300
          ${open && !selectedCity ? 'border-blue-300' : 'border-gray-300'}
          ${selectedCity ? 'border-gray-300 text-gray-900' : ''}
          ${hasError && !open ? 'border-danger-600 focus:border-danger-600' : ''}
          `}
            data-testid={DataTestIdEnum.SELECT_CITIES_BUTTON_TRIGGER_SELECT_CITIES}
          >
            {selectedCity ? <>{`${selectedCity.city} - ${selectedCity.uf}`}</> : <>Selecione uma opção</>}
          </Button>
        </DrawerTrigger>
        <DrawerContent className="flex min-h-[50%]">
          <DrawerHeader>
            <DrawerTitle className="flex text-lg">Cidade</DrawerTitle>
          </DrawerHeader>
          <div className="border-t">
            <StatusList setOpen={setOpen} setSelectedCity={onChange} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  },
);

SelectCities.displayName = 'SelectCities';

function StatusList({
  setOpen,
  setSelectedCity,
}: {
  setOpen: (open: boolean) => void;
  setSelectedCity: (city: City | null) => void;
}) {
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const debouncedInputValue = useDebounceValue(inputValue, 300);

  useEffect(() => {
    async function fetchCitiesByTerm() {
      if (debouncedInputValue.length >= 3) {
        setLoading(true);
        try {
          const data = await searchCitiesByTerm(debouncedInputValue);
          setCities(data.results);
        } catch (error) {
          const logger = getLogger('SelectCitiesComponent');
          logger.warn('Error fetching cities by term', { debouncedInputValue, error });
        } finally {
          setLoading(false);
        }
      } else {
        setCities([]);
      }
    }

    fetchCitiesByTerm();
  }, [debouncedInputValue]);

  const handleSelect = (value: string) => {
    const selectedCity = cities.find((city) => `${city.city} - ${city.uf}` === value) || null;
    setSelectedCity(selectedCity);
    setOpen(false);
  };

  return (
    <Command shouldFilter={false}>
      <CommandInput
        data-testid={DataTestIdEnum.SELECT_CITIES_COMMAND_INPUT}
        className="py-2"
        placeholder="Digite aqui"
        onChangeCapture={(e: { currentTarget: { value: string } }) => {
          const sanitizedValue = DOMPurify.sanitize(e.currentTarget.value);
          setInputValue(sanitizedValue);
        }}
      />
      <CommandList className="h-full">
        {inputValue.length > 0 && !loading && !cities.length && (
          <CommandEmpty data-testid={DataTestIdEnum.SELECT_CITIES_COMMAND_EMPTY} className="p-4 text-center">
            <span className="text-base text-gray-900">
              {inputValue.length > 2 ? 'Cidade não encontrada. Verifique o nome.' : 'Digite ao menos 3 caracteres'}
            </span>
          </CommandEmpty>
        )}
        {loading && (
          <CommandEmpty>
            <div className="loading loading-lg"></div>
          </CommandEmpty>
        )}
        {cities.length > 0 && (
          <CommandGroup className="h-full">
            {cities.map((city, index) => {
              const key = `${city.city} - ${city.uf}`;
              return (
                <CommandItem
                  data-testid={`${DataTestIdEnum.SELECT_CITIES_COMMAND_ITEM}-${index}`}
                  key={key}
                  value={key}
                  onSelect={handleSelect}
                  className="hover:cursor-pointer"
                >
                  {city.city}
                  {' '}
                  -
                  {city.uf}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
}

export default SelectCities;
